import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText, AlertTriangle, Clock, Shield, Brain, LayoutDashboard,
  ArrowRight, Sparkles, Activity, Search as SearchIcon, MessageSquare,
  TrendingUp, ChevronRight, ArrowUpRight, Eye, Zap, HardDrive, Cloud
} from 'lucide-react';
import { brandColors } from '../components/Brand';
import api from '../api/api';
import { toast } from 'sonner';
import { IntelligenceDashboard } from '../components/agentic';
import WorkflowInsightsWidget from '../components/widgets/WorkflowInsightsWidget';
import { motion } from 'framer-motion';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalDocs: 0, riskyDocs: 0, pendingApprovals: 0, recentDocs: [],
    complianceScore: 0, aiProcessed: 0, lowRisk: 0, medRisk: 0, highRisk: 0
  });
  const [loading, setLoading] = useState(true);
  const [storageInfo, setStorageInfo] = useState(null);

  const isSuperAdmin = user?.roles?.includes('SuperAdmin');

  useEffect(() => {
    document.title = 'ReasonDMS | Dashboard';
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.getStorageInfo(getAuthHeaders())
      .then((r) => setStorageInfo(r.data))
      .catch(() => setStorageInfo(null));
    // eslint-disable-next-line
  }, [isSuperAdmin]);

  const loadDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      const docsResponse = await api.listDocuments({}, headers);
      const docs = docsResponse.data;
      let tasks = [];
      try {
        const tasksResponse = await api.getMyTasks(headers);
        tasks = tasksResponse.data?.instances || [];
      } catch (e) { /* Tasks not available */ }

      const lowRisk = docs.filter(d => d.risk_level === 'Low' || !d.risk_level).length;
      const medRisk = docs.filter(d => d.risk_level === 'Medium').length;
      const highRisk = docs.filter(d => d.risk_level === 'High').length;
      const total = docs.length || 1;
      const complianceScore = Math.round(((lowRisk * 1.0 + medRisk * 0.5) / total) * 100);
      const aiProcessed = docs.filter(d => d.metadata?.__ai_insight || d.__ai_insight).length;

      setStats({
        totalDocs: docs.length, riskyDocs: docs.filter(d => d.risk_level === 'High' || d.risk_level === 'Medium').length,
        pendingApprovals: tasks.length, recentDocs: docs.slice(0, 5),
        complianceScore, aiProcessed, lowRisk, medRisk, highRisk
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error(t('errors.loadFailed'));
    } finally { setLoading(false); }
  };

  const fadeIn = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-transparent mx-auto" style={{ borderTopColor: brandColors.primary, borderRightColor: brandColors.primary }} />
          <p className="mt-4 text-sm text-slate-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const kpis = [
    {
      icon: FileText, title: 'Total Documents', value: stats.totalDocs,
      desc: 'In your workspace', color: brandColors.primary, borderColor: brandColors.primary,
      onClick: () => navigate('/documents')
    },
    {
      icon: AlertTriangle, title: 'At-Risk Documents', value: stats.riskyDocs,
      desc: `${stats.highRisk} high, ${stats.medRisk} medium`, color: '#dc6b20', borderColor: brandColors.accent,
      onClick: () => navigate('/documents')
    },
    {
      icon: Clock, title: 'Pending Actions', value: stats.pendingApprovals,
      desc: 'Awaiting your review', color: '#7c3aed', borderColor: '#7c3aed',
      onClick: () => navigate('/workflows')
    },
    {
      icon: Shield, title: 'Compliance Score',
      value: `${stats.complianceScore}%`,
      desc: `${stats.lowRisk} compliant of ${stats.totalDocs}`,
      color: stats.complianceScore >= 80 ? brandColors.secondary : stats.complianceScore >= 50 ? '#d97706' : '#dc2626',
      borderColor: stats.complianceScore >= 80 ? brandColors.secondary : stats.complianceScore >= 50 ? '#d97706' : '#dc2626',
    },
  ];

  const quickActions = [
    { icon: FileText, label: 'Upload Document', path: '/documents', color: brandColors.primary },
    { icon: SearchIcon, label: 'AI Search', path: '/search', color: brandColors.accent },
    { icon: MessageSquare, label: 'AI Assistant', path: '/chatbot', color: brandColors.secondary },
    { icon: Sparkles, label: 'AI Extensions', path: '/ai-extensions', color: '#7c3aed' },
    { icon: Eye, label: 'Vision AI', path: '/vision-intelligence', color: '#0891b2' },
    { icon: Zap, label: 'Enterprise AI', path: '/enterprise-intelligence', color: '#059669' },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Page Header */}
      <motion.div {...fadeIn} transition={{ duration: 0.4 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: brandColors.primary, fontFamily: 'var(--font-heading)' }}>
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Welcome back, {user?.full_name || 'User'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="hero-upload-btn"
            onClick={() => navigate('/documents')}
            className="shadow-sm hover:-translate-y-0.5 transition-all duration-200 text-sm"
            style={{ backgroundColor: brandColors.accent }}
          >
            <FileText className="w-4 h-4 mr-1.5" /> Upload
          </Button>
          <Button
            data-testid="hero-search-btn"
            onClick={() => navigate('/search')}
            variant="outline"
            className="border-slate-200 text-slate-700 hover:bg-slate-50 text-sm"
          >
            <SearchIcon className="w-4 h-4 mr-1.5" /> Search
          </Button>
        </div>
      </motion.div>

      {/* Dashboard Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-xs bg-slate-100 p-0.5 rounded-lg h-9">
          <TabsTrigger value="overview" data-testid="tab-overview" className="flex items-center gap-1.5 text-xs rounded-md">
            <LayoutDashboard className="w-3.5 h-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="ai-intelligence" data-testid="tab-ai-intelligence" className="flex items-center gap-1.5 text-xs rounded-md">
            <Brain className="w-3.5 h-3.5" /> AI Intelligence
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-5">
          {/* SuperAdmin Storage Health indicator */}
          {isSuperAdmin && storageInfo && (
            <div
              data-testid="storage-health-indicator"
              className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-3"
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${storageInfo.mode === 's3' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  {storageInfo.mode === 's3'
                    ? <Cloud className="w-5 h-5 text-emerald-600" />
                    : <HardDrive className="w-5 h-5 text-amber-600" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Storage Health</p>
                  <p className="text-xs text-slate-500">
                    {storageInfo.mode === 's3'
                      ? `AWS S3 — bucket: ${storageInfo.bucket || 'n/a'}`
                      : `Local filesystem — ${storageInfo.local_path || 'n/a'}`}
                  </p>
                </div>
              </div>
              <span
                data-testid="storage-mode-badge"
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  storageInfo.mode === 's3' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}
              >
                {storageInfo.mode === 's3' ? 'S3 Connected' : 'Local Mode'}
              </span>
            </div>
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((kpi, idx) => {
              const Icon = kpi.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                >
                  <Card
                    data-testid={`kpi-card-${idx}`}
                    className={`border border-slate-200 bg-white hover:shadow-md transition-all duration-200 overflow-hidden ${kpi.onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}
                    style={{ borderLeftWidth: '3px', borderLeftColor: kpi.borderColor }}
                    onClick={kpi.onClick}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{kpi.title}</p>
                          <p className="text-3xl font-bold mt-1.5 tracking-tight" style={{ color: kpi.color, fontFamily: 'var(--font-heading)' }}>
                            {kpi.value}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">{kpi.desc}</p>
                        </div>
                        <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${kpi.color}08` }}>
                          <Icon className="w-5 h-5" style={{ color: kpi.color }} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Documents - 2 cols */}
            <motion.div className="lg:col-span-2" {...fadeIn} transition={{ duration: 0.4, delay: 0.3 }}>
              <Card className="border border-slate-200 bg-white h-full">
                <CardHeader className="pb-3 pt-5 px-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full" style={{ backgroundColor: brandColors.primary }} />
                      <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>
                        Recent Documents
                      </CardTitle>
                    </div>
                    <Button
                      onClick={() => navigate('/documents')}
                      variant="ghost"
                      size="sm"
                      data-testid="view-all-docs-btn"
                      className="text-xs text-slate-500 hover:text-slate-700 h-7 px-2"
                    >
                      View all <ChevronRight className="w-3 h-3 ml-0.5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  {stats.recentDocs.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium text-slate-500">No documents yet</p>
                      <p className="text-xs text-slate-400 mt-1">Upload your first document to get started</p>
                      <Button
                        onClick={() => navigate('/documents')}
                        className="mt-4 text-xs"
                        size="sm"
                        data-testid="empty-upload-btn"
                        style={{ backgroundColor: brandColors.primary }}
                      >
                        Upload Document
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {stats.recentDocs.map((doc) => (
                        <div
                          key={doc.id}
                          data-testid={`recent-doc-${doc.id}`}
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group"
                          onClick={() => navigate(`/documents/${doc.id}`)}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 rounded-lg bg-slate-50 group-hover:bg-white border border-transparent group-hover:border-slate-100 transition-all shrink-0">
                              <FileText className="w-4 h-4" style={{ color: brandColors.primary }} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800 truncate">{doc.title}</p>
                              <p className="text-xs text-slate-400">{doc.doc_type}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                              doc.risk_level === 'High' ? 'bg-red-50 text-red-600 border-red-100' :
                              doc.risk_level === 'Medium' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                              'bg-emerald-50 text-emerald-600 border-emerald-100'
                            }`}>
                              {doc.risk_level || 'Low'}
                            </span>
                            <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Workflow Insights Widget */}
              <WorkflowInsightsWidget headers={getAuthHeaders()} />

              {/* Quick Actions */}
              <motion.div {...fadeIn} transition={{ duration: 0.4, delay: 0.4 }}>
                <Card className="border border-slate-200 bg-white">
                  <CardHeader className="pb-3 pt-5 px-5">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full" style={{ backgroundColor: brandColors.accent }} />
                      <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>
                        Quick Actions
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 space-y-1.5">
                    {quickActions.map(({ icon: Icon, label, path, color }) => (
                      <Button
                        key={path}
                        onClick={() => navigate(path)}
                        data-testid={`quick-action-${path.replace('/', '')}`}
                        className="w-full justify-start h-9 text-xs font-medium group"
                        variant="ghost"
                      >
                        <div className="p-1.5 rounded-md mr-2.5 transition-colors" style={{ backgroundColor: `${color}10` }}>
                          <Icon className="w-3.5 h-3.5" style={{ color }} />
                        </div>
                        <span className="text-slate-700 group-hover:text-slate-900">{label}</span>
                        <ArrowRight className="w-3 h-3 ml-auto text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>

              {/* AI Status */}
              {stats.totalDocs > 0 && (
                <motion.div {...fadeIn} transition={{ duration: 0.4, delay: 0.5 }}>
                  <Card className="border border-slate-200 bg-white">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-1 h-5 rounded-full" style={{ backgroundColor: brandColors.secondary }} />
                        <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>AI Processing</p>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">
                          {stats.aiProcessed} of {stats.totalDocs} processed
                        </p>
                        <span className="text-xs font-semibold" style={{ color: brandColors.secondary }}>
                          {stats.totalDocs > 0 ? Math.round((stats.aiProcessed / stats.totalDocs) * 100) : 0}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${stats.totalDocs > 0 ? (stats.aiProcessed / stats.totalDocs) * 100 : 0}%`,
                            backgroundColor: brandColors.secondary
                          }}
                        />
                      </div>

                      {/* Risk breakdown mini */}
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        <div className="text-center p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                          <p className="text-lg font-bold text-emerald-600" style={{ fontFamily: 'var(--font-heading)' }}>{stats.lowRisk}</p>
                          <p className="text-[10px] text-emerald-500 font-medium">Low Risk</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-100">
                          <p className="text-lg font-bold text-amber-600" style={{ fontFamily: 'var(--font-heading)' }}>{stats.medRisk}</p>
                          <p className="text-[10px] text-amber-500 font-medium">Medium</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-red-50 border border-red-100">
                          <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'var(--font-heading)' }}>{stats.highRisk}</p>
                          <p className="text-[10px] text-red-500 font-medium">High Risk</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* AI Intelligence Tab */}
        <TabsContent value="ai-intelligence" className="mt-5">
          <IntelligenceDashboard headers={getAuthHeaders()} timeRangeDays={30} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
